import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import ShieldImg from "../../Assets/Icons/shield.png";
import AdminTournamentDraw from "./AdminTournamentDraw";

const AdminAddTeams = ({ tournamentId }) => {
  const availableGroupCounts = [2, 4, 8, 16];
  const allPossibleGroups = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(65 + i),
  );
  const [numberOfGroups, setNumberOfGroups] = useState(8);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDrawOpen, setIsDrawOpen] = useState(false);
  const [teams, setTeams] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isRosterShareModalOpen, setIsRosterShareModalOpen] = useState(false);
  const [turnir, setTurnir] = useState(null);
  // State za kreiranje novog tima
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamLogo, setNewTeamLogo] = useState(null);
  const [teamSuggestions, setTeamSuggestions] = useState([]);

  // State za uređivanje tima i igrača
  const [editingTeam, setEditingTeam] = useState(null);
  const [editTeamName, setEditTeamName] = useState("");
  const [editTeamLogo, setEditTeamLogo] = useState(null);
  const [playerInput, setPlayerInput] = useState({ no: "", name: "" });
  const [editingPlayerIndex, setEditingPlayerIndex] = useState(null);

  useEffect(() => {
    if (!tournamentId) return;

    supabase
      .from("admin_turniri")
      .select("*")
      .eq("id", tournamentId)
      .single()
      .then(({ data }) => {
        if (data) {
          setTurnir(data);
          if (data.seniori_broj_grupa) {
            setNumberOfGroups(data.seniori_broj_grupa);
          }
        }
      });
  }, [tournamentId]);

  const handleGroupCountChange = async (newCount) => {
    setNumberOfGroups(newCount);
    if (!tournamentId) return;

    await supabase
      .from("admin_turniri")
      .update({ seniori_broj_grupa: newCount })
      .eq("id", tournamentId);
  };

  const fetchTeams = async () => {
    if (!tournamentId) return;

    const { data: teamsData, error: teamsError } = await supabase
      .from("turniri_timovi")
      .select("*")
      .eq("turnir_id", tournamentId);

    const { data: matchesData, error: matchesError } = await supabase
      .from("turniri_utakmice")
      .select("*")
      .eq("tournament_id", tournamentId)
      .eq("is_completed", true);

    if (teamsError || matchesError) {
      console.error("Greška:", teamsError || matchesError);
      return;
    }

    const { data: turnirData } = await supabase
      .from("admin_turniri")
      .select("*")
      .eq("id", tournamentId)
      .single();

    if (turnirData) {
      setTurnir(turnirData);
    }

    const stats = {};
    teamsData.forEach((t) => {
      stats[t.id] = {
        ...t,
        played: 0,
        points: 0,
        goals_for: 0,
        goals_against: 0,
      };
    });

    matchesData.forEach((m) => {
      const tA = stats[m.team_a_id];
      const tB = stats[m.team_b_id];
      if (tA && tB) {
        tA.played += 1;
        tB.played += 1;
        tA.goals_for += m.scorea;
        tA.goals_against += m.scoreb;
        tB.goals_for += m.scoreb;
        tB.goals_against += m.scorea;
        if (m.scorea > m.scoreb) tA.points += 3;
        else if (m.scorea < m.scoreb) tB.points += 3;
        else {
          tA.points += 1;
          tB.points += 1;
        }
      }
    });

    const processedTeams = Object.values(stats).sort(
      (a, b) =>
        b.points - a.points ||
        b.goals_for - b.goals_against - (a.goals_for - a.goals_against) ||
        b.goals_for - a.goals_for,
    );

    setTeams(processedTeams);
  };

  useEffect(() => {
    if (!tournamentId) return;

    const fetchTeams = async () => {
      // Timovi koriste turnir_id
      const { data: teamsData, error: teamsError } = await supabase
        .from("turniri_timovi")
        .select("*")
        .eq("turnir_id", tournamentId);

      // Utakmice koriste tournament_id (zato je ovdje tournament_id)
      const { data: matchesData, error: matchesError } = await supabase
        .from("turniri_utakmice")
        .select("*")
        .eq("tournament_id", tournamentId)
        .eq("is_completed", true);

      if (teamsError || matchesError) {
        console.error("Greška:", teamsError || matchesError);
        return;
      }

      const stats = {};
      teamsData.forEach((t) => {
        stats[t.id] = {
          ...t,
          played: 0,
          points: 0,
          goals_for: 0,
          goals_against: 0,
        };
      });

      matchesData.forEach((m) => {
        const tA = stats[m.team_a_id];
        const tB = stats[m.team_b_id];
        if (tA && tB) {
          tA.played += 1;
          tB.played += 1;
          tA.goals_for += m.scorea;
          tA.goals_against += m.scoreb;
          tB.goals_for += m.scoreb;
          tB.goals_against += m.scorea;
          if (m.scorea > m.scoreb) tA.points += 3;
          else if (m.scorea < m.scoreb) tB.points += 3;
          else {
            tA.points += 1;
            tB.points += 1;
          }
        }
      });

      const processedTeams = Object.values(stats).sort(
        (a, b) =>
          b.points - a.points ||
          b.goals_for - b.goals_against - (a.goals_for - a.goals_against) ||
          b.goals_for - a.goals_for,
      );

      setTeams(processedTeams);
    };

    fetchTeams();
  }, [tournamentId]);

  const groups = allPossibleGroups.slice(0, numberOfGroups);

  useEffect(() => {
    if (editingTeam) {
      const current = teams.find((t) => t.id === editingTeam.id);
      if (current && JSON.stringify(current) !== JSON.stringify(editingTeam)) {
        setEditingTeam(current);
      }
    }
  }, [teams, editingTeam]);

  const uploadLogo = async (file) => {
    const compressedFile = await compressImage(file);
    const fileName = `${Math.random()}-${Date.now()}.webp`;
    const filePath = `team-logos/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("logos")
      .upload(filePath, compressedFile);

    if (uploadError) throw uploadError;
    const { data } = supabase.storage.from("logos").getPublicUrl(filePath);
    return data.publicUrl;
  };
  const handleSaveNewTeam = async () => {
    if (!newTeamName.trim()) return alert("Upišite ime tima!");
    if (!tournamentId) return alert("Greška: ID turnira nije pronađen!");
    if (!selectedGroup) return alert("Greška: Grupa nije odabrana!");

    setIsUploading(true);
    try {
      let finalLogoUrl = null;

      if (typeof newTeamLogo === "string") {
        finalLogoUrl = newTeamLogo;
      } else if (newTeamLogo) {
        finalLogoUrl = await uploadLogo(newTeamLogo);
      }

      // 1. Spremi tim na trenutni turnir
      const { error: insertError } = await supabase
        .from("turniri_timovi")
        .insert([
          {
            turnir_id: tournamentId,
            name: newTeamName.trim(),
            group_name: selectedGroup,
            logo_url: finalLogoUrl,
            players: [],
          },
        ]);

      if (insertError) throw insertError;

      // 2. Spremi ili ažuriraj u globalnu tablicu admin_all_teams ako već ne postoji
      const { data: existingGlobal } = await supabase
        .from("admin_all_teams")
        .select("id, logo_url")
        .ilike("name", newTeamName.trim())
        .maybeSingle();

      if (!existingGlobal) {
        await supabase.from("admin_all_teams").insert([
          {
            name: newTeamName.trim(),
            logo_url: finalLogoUrl,
          },
        ]);
      } else if (finalLogoUrl && !existingGlobal.logo_url) {
        await supabase
          .from("admin_all_teams")
          .update({ logo_url: finalLogoUrl })
          .eq("id", existingGlobal.id);
      }

      setIsTeamModalOpen(false);
      setNewTeamName("");
      setNewTeamLogo(null);
      setTeamSuggestions([]);
      await fetchTeams();
    } catch (err) {
      alert("Greška kod spremanja tima: " + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const openEditModal = (team) => {
    setEditingTeam(team);
    setEditTeamName(team.name);
    setEditTeamLogo(team.logo_url);
    setPlayerInput({ no: "", name: "" });
    setEditingPlayerIndex(null);
    setIsEditModalOpen(true);
  };

  const compressImage = (file, maxWidth = 300, maxHeight = 300) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], `${Date.now()}.webp`, {
                  type: "image/webp",
                });
                resolve(compressedFile);
              } else {
                reject(new Error("Kompresija nije uspjela."));
              }
            },
            "image/webp",
            0.8,
          );
        };
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleUpdateTeamData = async () => {
    if (!editTeamName) return alert("Ime tima ne može biti prazno!");
    setIsUploading(true);
    try {
      let finalLogoUrl = editingTeam.logo_url;
      if (editTeamLogo && typeof editTeamLogo !== "string") {
        finalLogoUrl = await uploadLogo(editTeamLogo);
      }

      const { error } = await supabase
        .from("turniri_timovi")
        .update({ name: editTeamName, logo_url: finalLogoUrl })
        .eq("id", editingTeam.id);

      if (error) throw error;
      alert("Uspješno spremljeno!");
      fetchTeams();
    } catch (err) {
      alert(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const formatPlayerName = (fullName) => {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 0) return "";
    return parts
      .map((part, index) => {
        if (index === parts.length - 1) {
          return part;
        }

        return `${part.charAt(0).toUpperCase()}.`;
      })
      .join(" ");
  };

  const handleSavePlayer = async () => {
    if (!playerInput.name || !playerInput.no)
      return alert("Popunite polja za igrača!");

    const formattedName = formatPlayerName(playerInput.name);

    let updatedPlayers = [...(editingTeam.players || [])];

    if (editingPlayerIndex !== null) {
      updatedPlayers[editingPlayerIndex] = {
        ...updatedPlayers[editingPlayerIndex],
        name: formattedName,
        no: parseInt(playerInput.no),
      };
    } else {
      updatedPlayers.push({
        name: formattedName,
        no: parseInt(playerInput.no),
        goals: 0,
      });
    }

    const { error } = await supabase
      .from("turniri_timovi")
      .update({ players: updatedPlayers })
      .eq("id", editingTeam.id);

    if (!error) {
      setPlayerInput({ no: "", name: "" });
      setEditingPlayerIndex(null);
      fetchTeams();
    } else {
      alert("Greška: " + error.message);
    }
  };

  const handleDeletePlayer = async (index) => {
    if (!window.confirm("Obrisati igrača?")) return;
    let updatedPlayers = [...(editingTeam.players || [])];
    updatedPlayers.splice(index, 1);

    const { error } = await supabase
      .from("turniri_timovi")
      .update({ players: updatedPlayers })
      .eq("id", editingTeam.id);

    if (!error) {
      fetchTeams();
    } else {
      alert("Greška: " + error.message);
    }
  };

  const handleDeleteTeam = async (id) => {
    if (window.confirm("Obrisati cijeli tim i sve igrače?")) {
      await supabase.from("turniri_timovi").delete().eq("id", id);
      setIsEditModalOpen(false);
      setEditingTeam(null);
      fetchTeams();
    }
  };

  const groupTeams = teams.filter((t) => t.group_name === selectedGroup);

  return (
    <div className="admin-teams-container">
      {!selectedGroup && (
        <div className="group-count-selector-bar">
          <label htmlFor="numberOfGroupsSelect">Broj grupa:</label>
          <select
            id="numberOfGroupsSelect"
            name="numberOfGroups"
            value={numberOfGroups}
            onChange={(e) => handleGroupCountChange(parseInt(e.target.value))}
          >
            {availableGroupCounts.map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
            {Array.from({ length: 16 }, (_, i) => i + 1)
              .filter((n) => n > 16)
              .map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
          </select>
        </div>
      )}
      {!selectedGroup ? (
        <div className="groups-grid">
          {groups.map((g) => {
            const count = teams.filter((t) => t.group_name === g).length;
            return (
              <div
                key={g}
                className="group-card"
                onClick={() => setSelectedGroup(g)}
              >
                <div className="group-card-badge">GRUPA</div>
                <h3>{g}</h3>
                <div className="group-card-badge-float">{g}</div>
                <span className="team-count">
                  <i className="fi fi-sr-users-alt"></i>Broj ekipa:{" "}
                  <strong>{count}</strong>
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="group-detail">
          <button
            className="back-btn-simple"
            onClick={() => setSelectedGroup(null)}
          >
            <i className="fi fi-rs-angle-left"></i> Popis grupa
          </button>

          <div className="header-row">
            <h3>GRUPA {selectedGroup}</h3>
            <button
              className="add-btn-main"
              onClick={() => setIsTeamModalOpen(true)}
            >
              <i className="fi fi-rs-plus-small"></i> DODAJ TIM
            </button>
          </div>

          {groupTeams.length === 0 ? (
            <div className="no-teams-placeholder">
              <i className="fi fi-rs-shield-check"></i>
              <p>Još nema dodanih timova u ovoj grupi.</p>
            </div>
          ) : (
            <div className="table-responsive-wrapper">
              <table className="teams-table-preview">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Ekipa</th>
                    <th className="text-center">UT</th>
                    <th className="text-center">GR</th>
                    <th className="text-center">BOD</th>
                    <th className="text-right">Akcija</th>
                  </tr>
                </thead>
                <tbody>
                  {groupTeams.map((team, index) => {
                    const goalDiff = team.goals_for - team.goals_against;
                    return (
                      <tr key={team.id}>
                        <td className="team-position">{index + 1}.</td>
                        <td>
                          <div
                            className="team-table-identity"
                            onClick={() => openEditModal(team)}
                          >
                            <img src={team.logo_url || ShieldImg} alt="logo" />
                            <span className="team-name-txt">{team.name}</span>
                          </div>
                        </td>
                        <td className="text-center text-muted-table">
                          {team.played}
                        </td>
                        <td className="text-center text-muted-table">
                          {goalDiff > 0 ? `+${goalDiff}` : goalDiff}
                        </td>
                        <td className="text-center team-points-cell">
                          {team.points}
                        </td>
                        <td className="text-right">
                          <button
                            onClick={() => openEditModal(team)}
                            className="action-edit-btn"
                          >
                            <i className="fi fi-rs-settings"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {/* MODAL ZA DODAVANJE NOVOG TIMA */}
      {isTeamModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => {
            setIsTeamModalOpen(false);
            setTeamSuggestions([]);
          }}
        >
          <div
            className="event-modal animate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-container">
              <h3>Novi Tim (Grupa {selectedGroup})</h3>
              <i
                className="fi fi-rs-cross close-modal-icon"
                onClick={() => {
                  setIsTeamModalOpen(false);
                  setTeamSuggestions([]);
                }}
              ></i>
            </div>

            <div className="form-group" style={{ position: "relative" }}>
              <label htmlFor="newTeamNameInput">Naziv Tima</label>
              <input
                id="newTeamNameInput"
                name="newTeamName"
                type="text"
                placeholder="Unesi ime ekipe (npr. AGRAD)..."
                value={newTeamName}
                onChange={async (e) => {
                  const val = e.target.value;
                  setNewTeamName(val);

                  if (val.trim().length > 1) {
                    // Dohvaćamo iz globalne tablice admin_all_teams
                    const { data } = await supabase
                      .from("admin_all_teams")
                      .select("name, logo_url")
                      .ilike("name", `%${val}%`)
                      .limit(5);

                    // Filtriramo da se spriječe duplikati na istom turniru
                    const existingTeamNames = teams.map((t) =>
                      t.name.toLowerCase(),
                    );
                    const filtered = (data || []).filter(
                      (s) => !existingTeamNames.includes(s.name.toLowerCase()),
                    );

                    setTeamSuggestions(filtered);
                  } else {
                    setTeamSuggestions([]);
                  }
                }}
              />

              {/* Padajući izbornik s prijedlozima */}
              {teamSuggestions.length > 0 && (
                <ul>
                  {teamSuggestions.map((s, idx) => (
                    <li
                      key={idx}
                      onClick={() => {
                        setNewTeamName(s.name);
                        if (s.logo_url) {
                          setNewTeamLogo(s.logo_url);
                        }
                        setTeamSuggestions([]);
                      }}
                    >
                      {s.logo_url && <img src={s.logo_url} alt="Logo ekipe" />}
                      <span>{s.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="form-group">
              <label>Grb / Logo</label>
              <label htmlFor="new-logo-upload" className="custom-file-upload">
                {newTeamLogo ? (
                  <div className="logo-preview-badge">
                    {typeof newTeamLogo === "string" ? (
                      <img
                        src={newTeamLogo}
                        alt="Logo"
                        style={{ width: 20, height: 20, objectFit: "contain" }}
                      />
                    ) : (
                      <i
                        className="fi fi-rs-check-circle"
                        style={{ color: "#2ecc71" }}
                      ></i>
                    )}
                    <span>
                      {typeof newTeamLogo === "string"
                        ? "Logo učitan"
                        : "Slika uspješno dodana"}
                    </span>
                  </div>
                ) : (
                  <>
                    <i className="fi fi-rs-plus-small"></i> Dodaj sliku
                  </>
                )}
              </label>
              <input
                name="newLogoFile"
                id="new-logo-upload"
                type="file"
                accept="image/*"
                onChange={(e) => setNewTeamLogo(e.target.files[0])}
                style={{ display: "none" }}
              />
            </div>

            <button
              className="add-btn"
              onClick={handleSaveNewTeam}
              disabled={isUploading}
            >
              {isUploading ? "Spremanje..." : "SPREMI TIM"}
            </button>
          </div>
        </div>
      )}
      {/* MODAL ZA UREĐIVANJE TIMA */}
      {isEditModalOpen && editingTeam && (
        <div
          className="modal-overlay"
          onClick={() => {
            setIsEditModalOpen(false);
            setEditingTeam(null);
          }}
        >
          <div
            className="event-modal edit-team-modern-modal animate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-container">
              <div className="modal-title-with-img">
                <img
                  src={editingTeam.logo_url || ShieldImg}
                  alt="logo-preview"
                />
                <h3>Uredi tim: {editingTeam.name}</h3>
              </div>
              <i
                className="fi fi-rs-cross close-modal-icon"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingTeam(null);
                }}
              ></i>
            </div>

            <div className="modal-scrollable-content">
              {/* STATISTIKA */}
              <div className="stats-readonly-grid">
                <div className="s-item">
                  <span>Odigrano</span>
                  <strong>{editingTeam.played}</strong>
                </div>
                <div className="s-item">
                  <span>GR</span>
                  <strong>
                    {editingTeam.goals_for - editingTeam.goals_against}
                  </strong>
                </div>
                <div className="s-item accent">
                  <span>Bodovi</span>
                  <strong>{editingTeam.points}</strong>
                </div>
                <div className="s-item">
                  <span>Grupa</span>
                  <strong>{editingTeam.group_name}</strong>
                </div>
              </div>

              {/* PODACI O KLUBU */}
              <div className="modern-section-box">
                <h4>
                  <i className="fi fi-rs-shield"></i> Informacije o klubu
                </h4>
                <div className="form-group-row">
                  <div className="form-group flex-2">
                    <label htmlFor="editTeamNameInput">Naziv ekipe</label>
                    <input
                      id="editTeamNameInput"
                      name="editTeamName"
                      type="text"
                      value={editTeamName}
                      onChange={(e) => setEditTeamName(e.target.value)}
                    />
                  </div>
                  <div className="form-group flex-1">
                    <label>Grb kluba</label>
                    <label
                      htmlFor="edit-logo-upload"
                      className="custom-file-upload"
                    >
                      {editTeamLogo && typeof editTeamLogo === "string" ? (
                        <div className="logo-preview-badge">
                          <img src={editTeamLogo} alt="Preview" />
                          <span>Promijeni sliku</span>
                        </div>
                      ) : editTeamLogo && typeof editTeamLogo === "object" ? (
                        <div className="logo-preview-badge">
                          <span>Odabrana nova slika</span>
                        </div>
                      ) : (
                        <>
                          <i className="fi fi-rs-plus-small"></i> Dodaj sliku
                        </>
                      )}
                    </label>
                    <input
                      name="editLogoFile"
                      id="edit-logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setEditTeamLogo(e.target.files[0])}
                      style={{ display: "none" }}
                    />
                  </div>
                </div>
                <button
                  className="inline-update-btn"
                  onClick={handleUpdateTeamData}
                  disabled={isUploading}
                >
                  {isUploading ? "Spremanje..." : "Spremi promjene"}
                </button>
              </div>

              {/* SASTAV TIMA */}
              <div className="modern-section-box">
                <h4>
                  <i className="fi fi-rs-users"></i> Sastav tima (
                  {editingTeam.players?.length || 0})
                </h4>
                <label htmlFor="playerNumberInput">Broj i ime igrača: </label>
                <br />
                <div className="player-fast-insert-form">
                  <input
                    id="playerNumberInput"
                    name="playerNumber"
                    type="number"
                    placeholder="Broj"
                    className="inp-no"
                    value={playerInput.no}
                    onChange={(e) =>
                      setPlayerInput({ ...playerInput, no: e.target.value })
                    }
                  />
                  <input
                    id="playerNameInput"
                    name="playerName"
                    type="text"
                    placeholder="npr. K. Horvat"
                    className="inp-name"
                    value={playerInput.name}
                    onChange={(e) =>
                      setPlayerInput({ ...playerInput, name: e.target.value })
                    }
                  />
                  <button
                    className="btn-player-action"
                    onClick={handleSavePlayer}
                  >
                    {editingPlayerIndex !== null ? "Spremi" : "Dodaj"}
                  </button>
                  {editingPlayerIndex !== null && (
                    <button
                      className="btn-player-cancel"
                      onClick={() => {
                        setEditingPlayerIndex(null);
                        setPlayerInput({ no: "", name: "" });
                      }}
                    >
                      X
                    </button>
                  )}
                </div>

                <div className="players-list-modern-wrapper">
                  {!editingTeam.players || editingTeam.players.length === 0 ? (
                    <p className="empty-players-label">
                      Nema dodanih igrača u timu.
                    </p>
                  ) : (
                    editingTeam.players.map((p, idx) => (
                      <div
                        key={idx}
                        className={`player-modern-row ${editingPlayerIndex === idx ? "under-edit" : ""}`}
                      >
                        <div className="p-info">
                          <span className="p-number">#{p.no}</span>
                          <span className="p-name">{p.name}</span>
                        </div>
                        <div className="p-actions">
                          <button
                            className="act-btn e"
                            onClick={() => {
                              setEditingPlayerIndex(idx);
                              setPlayerInput({
                                no: p.no.toString(),
                                name: p.name,
                              });
                            }}
                          >
                            <i className="fi fi-rs-pencil"></i>
                          </button>
                          <button
                            className="act-btn d"
                            onClick={() => handleDeletePlayer(idx)}
                          >
                            <i className="fi fi-rs-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* BRISANJE TIMA */}
              <div className="danger-zone-modern">
                <button
                  className="btn-delete-team-destructive"
                  onClick={() => handleDeleteTeam(editingTeam.id)}
                >
                  <i className="fi fi-rs-trash"></i> Obriši ovaj tim i sve
                  podatke
                </button>
              </div>
            </div>
          </div>
        </div>
      )}{" "}
      {turnir?.teams_draw === true && (
        <button className="btn-draw" onClick={() => setIsDrawOpen(true)}>
          <i className="fi fi-rr-dice-d12"></i>
          <span> Ždrijeb</span>
        </button>
      )}
      {isDrawOpen && (
        <AdminTournamentDraw
          turnir={turnir}
          onClose={() => setIsDrawOpen(false)}
          onFinish={() => {
            setIsDrawOpen(false);
            window.location.reload();
          }}
        />
      )}
      {/* Moderan lebdeći gumb */}
      {groups.every(
        (g) => teams.filter((t) => t.group_name === g).length >= 2,
      ) && (
        <button
          className="admin-floating-share-btn"
          onClick={() => setIsRosterShareModalOpen(true)}
        >
          <div className="btn-glow-effect"></div>
          <i className="fi fi-rr-task-checklist"></i>
        </button>
      )}
      {/* Moderan modal za dijeljenje */}
      {isRosterShareModalOpen && (
        <div
          className="admin-modal-backdrop"
          onClick={() => setIsRosterShareModalOpen(false)}
        >
          <div
            className="admin-roster-modal animate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-container">
              <div className="title-with-icon">
                <div className="modal-icon-wrapper">
                  <i className="fi fi-rs-users-alt"></i>
                </div>
                <div>
                  <h3>Popis igrača</h3>
                  <p>Upravljanje pristupom voditelja</p>
                </div>
              </div>
              <i
                className="fi fi-rs-cross close-modal-icon"
                onClick={() => setIsRosterShareModalOpen(false)}
              ></i>
            </div>

            <p className="roster-modal-desc">
              Podijelite ovu poveznicu s voditeljima ekipa kako bi sami
              jednostavno unijeli službeni popis igrača.
            </p>

            <div className="roster-credentials-card">
              <div className="cred-row">
                <span className="cred-label">PRISTUPNI PIN</span>
                <span className="cred-value pin-highlight">
                  {turnir?.roster_pin || "Nema PIN-a"}
                </span>
              </div>
              <div className="cred-divider"></div>
              <div className="cred-row column">
                <span className="cred-label">LINK ZA UNOS</span>
                <span className="cred-value link-text">
                  {window.location.origin}/{turnir?.slug}/popis-igraca?pin=
                  {turnir?.roster_pin}
                </span>
              </div>
            </div>

            <button
              className="admin-copy-action-btn"
              onClick={() => {
                const poruka = `UNOS POPISA IGRAČA - ${turnir?.naziv || "Turnir"}

Pozdrav! Molimo vas da ispunite službeni popis igrača za vašu ekipu putem sljedeće poveznice:

🔗 Link: ${window.location.origin}/${turnir?.slug}/popis-igraca?pin=${turnir?.roster_pin || ""}
🔑 PIN za pristup: *${turnir?.roster_pin || "Nema PIN-a"}*

Nakon otvaranja linka, odaberite svoju ekipu, unesite igrače i kliknite "Pošalji". Vidimo se na turniru!`;

                navigator.clipboard.writeText(poruka);
                alert("Poruka je uspješno kopirana!");
              }}
            >
              <i className="fi fi-rr-copy-alt"></i>
              Kopiraj poruku za voditelje
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAddTeams;
